import { portalApi, portalApiClient } from "../lib/portalApiClient";
import { usePortalAuthStore } from "../store/portalAuth.store";
import { requireDateOnlyString, type DateOnlyString } from "../utils/dateOnly";

export { triggerBlobDownload } from "../utils/downloadFilename";

export type DeliveredToSubjectType =
  | "physician"
  | "practice"
  | "surgery_center";

export interface DeliveredToSubject {
  type: DeliveredToSubjectType;
  id: string;
  name: string;
}

export type PortalReportStatus = "new" | "viewed" | "available";
export type PortalCaseStatus = "received_by_lab" | PortalReportStatus;

export interface PortalReportListItem {
  caseId: string;
  caseNumber: string;
  patientName: string;
  patientDob?: DateOnlyString | null;
  mrn?: string | null;
  reportDate: string;
  dateOfService?: DateOnlyString | null;
  physicianName?: string;
  practiceId?: string | null;
  practiceName?: string;
  surgeryCenter?: string | null;
  pathologist?: string | null;
  isNew: boolean;
  reportStatus: PortalReportStatus;
  reportStatusLabel: string;
  deliveredToSubject?: DeliveredToSubject | null;
}

export interface PortalReportListResponse {
  reports: PortalReportListItem[];
  total: number;
  page: number;
  pageSize: number;
  availableStatuses?: PortalReportStatus[];
  statusCounts?: {
    new: number;
    available: number;
    viewed: number;
    total: number;
  };
  availablePhysicians?: Array<{ id: string; name: string }>;
}

export interface PortalCaseListResponse {
  cases: PortalCaseListItem[];
  total: number;
  page: number;
  pageSize: number;
  availableStatuses: PortalCaseStatus[];
  statusCounts: {
    receivedByLab: number;
    new: number;
    available: number;
    viewed: number;
    total: number;
  };
  availablePhysicians: Array<{ id: string; name: string }>;
}

export interface PortalCaseListItem
  extends Omit<
    PortalReportListItem,
    "reportDate" | "reportStatus" | "reportStatusLabel"
  > {
  reportDate: string | null;
  reportStatus: PortalCaseStatus;
  reportStatusLabel: string;
  publicState: "received_by_lab";
  publicStateLabel: "Received by Lab";
  reportAvailable: boolean;
}

export interface PortalReportFilters {
  search?: string;
  practiceId?: string;
  dateFrom?: DateOnlyString;
  dateTo?: DateOnlyString;
  reportDateFrom?: DateOnlyString;
  reportDateTo?: DateOnlyString;
  status?: PortalReportStatus;
  physicianId?: string;
  mrn?: string;
  caseNumber?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export interface PortalCaseFilters extends Omit<PortalReportFilters, "status"> {
  status?: PortalCaseStatus;
}

export interface PortalBulkDownloadRequest {
  caseIds?: string[];
  filters?: Omit<PortalReportFilters, "page" | "pageSize">;
  includeViewed?: boolean;
}

export interface SupplyItemOption {
  itemKey: string;
  label: string;
  unit?: string | null;
}

export interface SupplyRequestItem {
  itemKey: string;
  label: string;
  quantity: number;
  unit?: string | null;
}

export interface SupplyRequestSubmission {
  surgeryCenterId?: string | null;
  surgeryCenterName: string;
  requesterName: string;
  requesterEmail: string;
  requesterPhone?: string | null;
  items: SupplyRequestItem[];
  neededByDate?: DateOnlyString | null;
  notes?: string | null;
}

export interface SupplyRequestResponse {
  accepted: boolean;
  message: string;
}

export interface PortalContactConfig {
  supportEmail: string;
  supportPhone?: string | null;
  missingCaseGuidance: string;
  supportCategories: string[];
  supplyItems: SupplyItemOption[];
}

export interface PortalIdentitySummary {
  email: string;
  subjectTypes: DeliveredToSubjectType[];
}

interface PortalDateFilterValues {
  readonly dateFrom?: DateOnlyString;
  readonly dateTo?: DateOnlyString;
  readonly reportDateFrom?: DateOnlyString;
  readonly reportDateTo?: DateOnlyString;
}

function normalizePortalDateFilters<T extends PortalDateFilterValues>(
  filters: T | undefined
): T | undefined {
  if (!filters) return undefined;
  const normalized = {
    ...filters,
    dateFrom: normalizeOptionalPortalDate(filters.dateFrom, "Date from"),
    dateTo: normalizeOptionalPortalDate(filters.dateTo, "Date to"),
    reportDateFrom: normalizeOptionalPortalDate(
      filters.reportDateFrom,
      "Report date from"
    ),
    reportDateTo: normalizeOptionalPortalDate(
      filters.reportDateTo,
      "Report date to"
    ),
  };
  assertPortalDateRange(normalized.dateFrom, normalized.dateTo, "Date");
  assertPortalDateRange(
    normalized.reportDateFrom,
    normalized.reportDateTo,
    "Report date"
  );
  return normalized;
}

function normalizeOptionalPortalDate(
  value: unknown,
  fieldName: string
): DateOnlyString | undefined {
  return value === undefined
    ? undefined
    : requireDateOnlyString(value, fieldName);
}

function assertPortalDateRange(
  from: DateOnlyString | undefined,
  to: DateOnlyString | undefined,
  label: string
): void {
  if (from && to && from > to) {
    throw new Error(`${label} from must not be after ${label} to`);
  }
}

function normalizeBulkReportRequest(
  request: PortalBulkDownloadRequest
): PortalBulkDownloadRequest {
  return request.filters
    ? { ...request, filters: normalizePortalDateFilters(request.filters) }
    : request;
}

function normalizeSupplyRequest(
  payload: SupplyRequestSubmission
): SupplyRequestSubmission {
  if (payload.neededByDate === undefined || payload.neededByDate === null) {
    return payload;
  }
  return {
    ...payload,
    neededByDate: requireDateOnlyString(payload.neededByDate, "Needed-by date"),
  };
}

export const physicianPortalService = {
  async getRegistrationData(invitationToken: string) {
    return portalApi.get<{
      email: string;
      practiceName: string;
      subjectName: string | null;
      alreadyRegistered: boolean;
      isValid: boolean;
    }>(`/auth/register/${invitationToken}`);
  },

  async requestOtp(email: string): Promise<{ sent: boolean }> {
    return portalApi.post("/auth/otp/request", { email });
  },

  async verifyOtp(
    email: string,
    code: string,
    invitationToken?: string
  ): Promise<{
    sessionToken: string;
    email: string;
    practiceNames: string[];
  }> {
    return portalApi.post("/auth/otp/verify", { email, code, invitationToken });
  },

  async skipPasskey(invitationToken: string): Promise<{
    sessionToken: string;
    email: string;
    practiceNames: string[];
  }> {
    return portalApi.post("/auth/register/skip", { invitationToken });
  },

  async getIdentity(): Promise<PortalIdentitySummary> {
    return portalApi.get<PortalIdentitySummary>("/dashboard/identity");
  },

  async getReports(params?: PortalReportFilters) {
    return portalApi.get<PortalReportListResponse>("/dashboard/reports", {
      params: normalizePortalDateFilters(params),
    });
  },

  async getCases(params?: PortalCaseFilters) {
    return portalApi.get<PortalCaseListResponse>("/dashboard/cases", {
      params: normalizePortalDateFilters(params),
    });
  },

  async bulkDownloadReports(request: PortalBulkDownloadRequest): Promise<Blob> {
    try {
      const res = await portalApiClient.post<Blob>(
        "/dashboard/reports/bulk-download",
        normalizeBulkReportRequest(request),
        { responseType: "blob" }
      );
      return res.data;
    } catch (error: any) {
      if (error.response?.data instanceof Blob) {
        try {
          const text =
            typeof error.response.data.text === "function"
              ? await error.response.data.text()
              : await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result as string);
                  reader.onerror = () =>
                    reject(reader.error || new Error("Failed to read blob"));
                  reader.readAsText(error.response.data);
                });
          const json = JSON.parse(text);
          if (json && json.error) {
            throw new Error(json.error);
          }
        } catch (innerError: any) {
          if (
            innerError.message &&
            !innerError.message.includes("is not valid JSON") &&
            !innerError.message.includes("JSON.parse")
          ) {
            throw innerError;
          }
          throw new Error("Failed to download reports. Please try again.");
        }
      }
      throw error;
    }
  },

  async bulkPrintReports(request: PortalBulkDownloadRequest): Promise<Blob> {
    try {
      const res = await portalApiClient.post<Blob>(
        "/dashboard/reports/bulk-pdf",
        normalizeBulkReportRequest(request),
        { responseType: "blob" }
      );
      return res.data;
    } catch (error: any) {
      if (error.response?.data instanceof Blob) {
        try {
          const text =
            typeof error.response.data.text === "function"
              ? await error.response.data.text()
              : await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result as string);
                  reader.onerror = () =>
                    reject(reader.error || new Error("Failed to read blob"));
                  reader.readAsText(error.response.data);
                });
          const json = JSON.parse(text);
          if (json && json.error) {
            throw new Error(json.error);
          }
        } catch (innerError: any) {
          if (
            innerError.message &&
            !innerError.message.includes("is not valid JSON") &&
            !innerError.message.includes("JSON.parse")
          ) {
            throw innerError;
          }
          throw new Error("Failed to generate bulk PDF. Please try again.");
        }
      }
      throw error;
    }
  },

  async markReportViewed(caseId: string): Promise<void> {
    return portalApi.post(`/dashboard/reports/${caseId}/view`, {});
  },

  getReportPdfUrl(caseId: string, download = false): string {
    const baseUrl = `${
      process.env.REACT_APP_API_BASE_URL || ""
    }/portal/dashboard/reports/${caseId}/pdf`;

    const params = new URLSearchParams();
    if (download) {
      params.append("download", "true");
    }

    const token = usePortalAuthStore.getState().sessionToken;
    if (token) {
      params.append("token", token);
    }

    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  },

  async listPasskeys() {
    return portalApi.get<any[]>("/account/passkeys");
  },

  async removePasskey(passkeyId: string): Promise<void> {
    return portalApi.delete(`/account/passkeys/${passkeyId}`);
  },

  async exchangeMagicLink(token: string): Promise<{
    sessionToken: string;
    email: string;
    practiceNames: string[];
    caseId: string | null;
  }> {
    return portalApi.post("/auth/magic/exchange", { token });
  },

  async getContactConfig(): Promise<PortalContactConfig> {
    return portalApi.get("/contact-config");
  },

  async submitSupplyRequest(
    payload: SupplyRequestSubmission
  ): Promise<SupplyRequestResponse> {
    return portalApi.post("/supply-requests", normalizeSupplyRequest(payload));
  },
};
